export interface WaitForTargetOptions {
  /** How long to keep watching before giving up. */
  timeoutMs?: number
  /** Checked whenever the DOM changes or the timeout fires; returning true
   * aborts the wait early (used to cancel an in-flight wait when the user
   * clicks Next/Back/Skip again before the previous step ever resolved). */
  isCancelled?: () => boolean
}

export type WaitForTargetResult =
  | { status: "found"; element: Element }
  | { status: "timeout" }
  | { status: "cancelled" }

const DEFAULT_TIMEOUT_MS = 4000
// Poll fallback: a sibling-route swap can unmount/remount across two commits and make MutationObserver miss the real mount (observed, not hypothetical); kept short since the CTA's loading spinner shows for the whole wait.
const FALLBACK_POLL_MS = 50

/**
 * Watches for `[data-guide-target="key"]` to exist rather than resolving
 * once and hoping — a step's real target can still be arriving (a fresh
 * route's fetch in flight, a tab whose content mounts a tick after the tab
 * itself switches). Reacts to real DOM mutations (a route change, a fetch
 * resolving) rather than depending on a timer alone — fast in production —
 * backed by a coarse poll so a missed mutation batch can never hang the
 * wait. Never silently succeeds with nothing found: the caller always gets
 * an explicit found/timeout/cancelled outcome to act on (typically falling
 * back to an explicit empty-state step, never a silent skip).
 */
export const waitForTarget = (
  key: string,
  options: WaitForTargetOptions = {}
): Promise<WaitForTargetResult> => {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, isCancelled } = options
  const selector = `[data-guide-target="${key}"]`

  return new Promise((resolve) => {
    let settled = false
    let observer: MutationObserver | null = null
    let timeoutId: number | null = null
    let pollId: number | null = null

    const cleanup = () => {
      observer?.disconnect()
      if (timeoutId !== null) window.clearTimeout(timeoutId)
      if (pollId !== null) window.clearInterval(pollId)
    }

    const settle = (result: WaitForTargetResult) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(result)
    }

    const check = () => {
      if (settled) return
      if (isCancelled?.()) {
        settle({ status: "cancelled" })
        return
      }
      const element = document.querySelector(selector)
      if (element) settle({ status: "found", element })
    }

    check()
    if (settled) return

    // The synchronous check above almost always misses a just-triggered
    // route change: `navigate()` doesn't flush React's re-render
    // synchronously, so the new route's target isn't in the DOM yet at this
    // exact line. Rather than lean on the poll interval to close that gap
    // (which visibly holds the CTA button in its loading state for up to a
    // full interval), recheck on the very next microtask and macrotask —
    // together these catch the render the instant it commits in the common
    // case, with the observer and poll below purely as the safety net for
    // when even that isn't fast enough.
    void Promise.resolve().then(check)
    window.setTimeout(check, 0)

    observer = new MutationObserver(check)
    observer.observe(document.body, { childList: true, subtree: true })
    pollId = window.setInterval(check, FALLBACK_POLL_MS)

    timeoutId = window.setTimeout(() => {
      check()
      if (!settled) settle({ status: "timeout" })
    }, timeoutMs)
  })
}
